import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from "react-router";
import { auth, applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from '../../firebase';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ErrorDisplay from '../../components/ErrorDisplay';
import * as theme from '../../theme';

export default function EmailActionHandler() {
  const [mode, setMode] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [continueUrl, setContinueUrl] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [passwordMatchError, setPasswordMatchError] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Parse query parameters
    const query = new URLSearchParams(location.search);
    const actionMode = query.get('mode');
    const code = query.get('oobCode');
    const apiKey = query.get('apiKey'); // Not always needed, but available
    const contUrl = query.get('continueUrl');

    if (!actionMode || !code) {
      setError('Invalid or missing action parameters. Please use the link from your email.');
      setLoading(false);
      return;
    }

    setMode(actionMode);
    setOobCode(code);
    setContinueUrl(contUrl || '/dashboard'); // Fallback to dashboard if no continueUrl

    // Handle based on mode
    switch (actionMode) {
      case 'resetPassword':
        // Verify the code and get the email
        verifyPasswordResetCode(auth, code)
          .then((verifiedEmail) => {
            setEmail(verifiedEmail);
            setLoading(false);
          })
          .catch((err) => {
            console.error('Verify reset code error:', err.code, err.message);
            setError('Invalid or expired reset code. Please request a new email.');
            setLoading(false);
          });
        break;
      case 'verifyEmail':
        // Automatically apply the verification
        applyActionCode(auth, code)
          .then(() => {
            setInfo('Email verified successfully!');
            setTimeout(() => navigate(contUrl || '/dashboard'), 3000);
          })
          .catch((err) => {
            console.error('Verify email error:', err.code, err.message);
            setError('Invalid or expired verification code. Please request a new email.');
          })
          .finally(() => setLoading(false));
        break;
      case 'recoverEmail':
        // Handle email recovery (e.g., revert email change)
        applyActionCode(auth, code)
          .then(() => {
            setInfo('Email recovered successfully!');
            setTimeout(() => navigate(contUrl || '/dashboard'), 3000);
          })
          .catch((err) => {
            console.error('Recover email error:', err.code, err.message);
            setError('Invalid or expired recovery code. Please request a new email.');
          })
          .finally(() => setLoading(false));
        break;
      default:
        setError('Unsupported action mode. Please try again or contact support.');
        setLoading(false);
    }
  }, [location.search, navigate]);

  const updatePasswordStrength = (password) => {
    let strength = '';
    if (password.length >= 8 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password) && /[^a-zA-Z\d]/.test(password)) {
      strength = 'Strong';
    } else if (password.length >= 8 && (/[a-z]/.test(password) || /[A-Z]/.test(password)) && /\d/.test(password)) {
      strength = 'Medium';
    } else if (password.length >= 8) {
      strength = 'Weak';
    }
    setPasswordStrength(strength);
  };

  const updatePasswordMatch = (confirm, password) => {
    setPasswordMatchError(password !== confirm ? 'Passwords do not match.' : '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'newPassword') {
      setNewPassword(value);
      updatePasswordStrength(value);
      updatePasswordMatch(confirmPassword, value);
    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);
      updatePasswordMatch(value, newPassword);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setInfo('Password reset successfully. Redirecting...');
      setTimeout(() => navigate(continueUrl), 3000);
    } catch (err) {
      console.error('Reset password error:', err.code, err.message);
      setError(err.message || 'Failed to reset password. Please request a new email.');
    }

    setLoading(false);
  };

  const clearError = () => setError('');
  const clearInfo = () => setInfo('');

  if (loading) {
    return (
      <div style={{ ...theme.Container.basicWide, marginTop: 20 }}>
        <Typography variant="h6">Processing your request...</Typography>
      </div>
    );
  }

  return (
    <div style={{ ...theme.Container.basicWide, marginTop: 20 }}>
      <div style={theme.Heading}>
        {mode === 'resetPassword' ? 'Reset Password' : mode === 'verifyEmail' ? 'Verify Email' : 'Email Action'}
      </div>
      <div style={theme.SubHeadingText}>
        {mode === 'resetPassword' ? `Enter your new password for ${email || 'your account'}.` : info || 'Handling your request...'}
      </div>
      <div style={{ marginBottom: 30 }}>
        <div style={{ marginBottom: 20 }}>
          <ErrorDisplay
            message={error || info}
            type={error ? 'error' : 'info'}
            clearMessage={error ? clearError : clearInfo}
          />
        </div>
        {mode === 'resetPassword' && (
          <Box
            component="form"
            onSubmit={handleResetPassword}
            sx={{ ...theme.FullForm.form, gap: 4, alignItems: 'center' }}
          >
            <TextField
              label="New Password"
              name="newPassword"
              type="password"
              value={newPassword}
              onChange={handleChange}
              variant="filled"
              required
              fullWidth
              helperText={passwordStrength ? `Strength: ${passwordStrength}` : 'Minimum 8 characters, include uppercase, lowercase, number, symbol for strong password.'}
            />
            <TextField
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={handleChange}
              variant="filled"
              required
              fullWidth
              error={!!passwordMatchError}
              helperText={passwordMatchError}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !oobCode || !email}
              sx={{ width: 200 }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
            <Button
              onClick={() => navigate('/sign-in')}
              variant="outlined"
              color="secondary"
              disabled={loading}
              sx={{ width: 200 }}
            >
              Return to Sign In
            </Button>
          </Box>
        )}
      </div>
    </div>
  );
}